import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import communityAPI from "../../../api/community";
import { useCommunityAuth } from "../../../contexts/CommunityAuthContext";
import CreatePost from "../shared/CreatePost";
import ChallengeCreate from "../challenges/ChallengeCreate";
import { LearningPathCreate } from "../learning/LearningPathManager";
import Card from "../../../componentes/ui/Card";
import Badge from "../../../componentes/ui/Badge";
import Avatar from "../../../componentes/ui/Avatar";
// import { useUserProfile } from "../../../hooks/useCommunity";
import { Pagination, Drawer, message } from "antd";
import AdminDashboard from "../Admin/AdminDashboard";
import { FullscreenSpinner } from '../../../componentes/LoadingIndicator';
import CertificatePreview from "../certificates/CertificatePreview";
import LoginPrompt from "../shared/LoginPrompt";
import { formatNumber } from "../../../utils/numberFormat";
import verifiedBadge from "../../../assets/verified.png";
import {
  ShieldCheck,
  ShieldAlert,
  Ban,
  Slash,
  Info,
  MegaphoneOff,
  MessageSquareOff,
  Users,
  UserPlus,
  Check,
  Plus,
  FileText,
  MessageCircle,
  Library,
  BookOpen,
  Award,
  ExternalLink,
} from "lucide-react";

export default function UserProfile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, loading: authLoading } = useCommunityAuth();
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [showCreate, setShowCreate] = useState(false);
  const [createType, setCreateType] = useState("blog");
  const [projectTab, setProjectTab] = useState("blog");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [showCertificate, setShowCertificate] = useState(false);
  const [contributionItems, setContributionItems] = useState([]);
  const [contributionsTotal, setContributionsTotal] = useState(0);
  const [contributionsPage, setContributionsPage] = useState(1);
  const [contributionsPageSize, setContributionsPageSize] = useState(5);
  const [contributionsLoading, setContributionsLoading] = useState(false);
  const [bookmarkItems, setBookmarkItems] = useState([]);
  const [bookmarksTotal, setBookmarksTotal] = useState(0);
  const [bookmarkPage, setBookmarkPage] = useState(1);
  const [bookmarkPageSize, setBookmarkPageSize] = useState(5);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);
  const FOLLOW_PAGE_SIZE = 10;
  const [followStats, setFollowStats] = useState({
    followers: 0,
    following: 0,
    isFollowing: false,
  });
  const [followLoading, setFollowLoading] = useState(false);
  const [followersDrawerOpen, setFollowersDrawerOpen] = useState(false);
  const [followingDrawerOpen, setFollowingDrawerOpen] = useState(false);
  const [followersData, setFollowersData] = useState([]);
  const [followingData, setFollowingData] = useState([]);
  const [followersTotal, setFollowersTotal] = useState(0);
  const [followingTotal, setFollowingTotal] = useState(0);
  const [followersPage, setFollowersPage] = useState(1);
  const [followingPage, setFollowingPage] = useState(1);
  const [followersLoading, setFollowersLoading] = useState(false);
  const [followingLoading, setFollowingLoading] = useState(false);
  const [loginPromptAction, setLoginPromptAction] = useState(null);

  const syncFollowStats = useCallback((source) => {
    if (!source) {
      setFollowStats({ followers: 0, following: 0, isFollowing: false });
      return;
    }
    setFollowStats({
      followers:
        typeof source.followers_count === "number" ? source.followers_count : 0,
      following:
        typeof source.following_count === "number" ? source.following_count : 0,
      isFollowing: Boolean(source.is_following),
    });
  }, []);

  const isAdminUser = (dashboard?.role || "").toLowerCase() === "admin";

  useEffect(() => {
    if (!isAdminUser && createType === "challenge") {
      setCreateType("blog");
    }
  }, [isAdminUser, createType]);

  useEffect(() => {
    // If a username is explicitly provided in the URL and it's not the owner,
    // redirect them to the public profile for that user.
    if (username && currentUser && username !== currentUser.username) {
      navigate(`/profile/${username}`, { replace: true });
      return;
    }

    const targetUsername = username || currentUser?.username;
    if (!targetUsername) {
      setDashboard(null);
      setError(null);
      setLoading(false);
      return;
    }
    fetchDashboard(targetUsername);
  }, [username, currentUser, navigate]);

  const fetchDashboard = async (uname) => {
    try {
      setLoading(true);
      setError(null);
      const res = await communityAPI.users.getDashboard(uname);
      setDashboard(res.data);
      syncFollowStats(res.data);
    } catch (err) {
      console.error("Error fetching dashboard:", err);
      setError("Unable to load profile data right now.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dashboard?.role === "admin") {
      fetchPendingCount();
    }
  }, [dashboard]);

  useEffect(() => {
    syncFollowStats(dashboard);
  }, [
    dashboard?.followers_count,
    dashboard?.following_count,
    dashboard?.is_following,
    syncFollowStats,
  ]);

  useEffect(() => {
    if (!dashboard?.username) {
      return;
    }
    setContributionItems([]);
    setContributionsTotal(0);
    setContributionsPage(1);
    setBookmarkItems([]);
    setBookmarksTotal(0);
    setBookmarkPage(1);
  }, [dashboard?.username]);

  useEffect(() => {
    if (activeTab === "projects") {
      setContributionsPage(1);
    }
  }, [activeTab, projectTab]);

  useEffect(() => {
    if (activeTab === "bookmarks") {
      setBookmarkPage(1);
    }
  }, [activeTab]);

  const fetchPendingCount = async () => {
    try {
      const { data } = await communityAPI.posts.getAll({
        page_size: 1,
        is_approved: false,
      });
      const count =
        typeof data?.count === "number"
          ? data.count
          : Array.isArray(data?.results)
            ? data.results.length
            : Array.isArray(data)
              ? data.length
              : 0;
      setPendingCount(count);
    } catch (err) {
      console.error("Failed to load pending count:", err);
    }
  };

  const handleFollowAction = async () => {
    if (!dashboard?.username) {
      return;
    }
    if (!currentUser) {
      setLoginPromptAction("follow");
      return;
    }
    if (isOwnProfile || followLoading) {
      return;
    }

    setFollowLoading(true);
    const action = followStats.isFollowing
      ? communityAPI.users.unfollow
      : communityAPI.users.follow;

    try {
      const { data } = await action(dashboard.username);
      syncFollowStats(data);
      setDashboard((prev) => {
        if (!prev) {
          return prev;
        }
        return {
          ...prev,
          followers_count:
            typeof data?.followers_count === "number"
              ? data.followers_count
              : prev.followers_count,
          following_count:
            typeof data?.following_count === "number"
              ? data.following_count
              : prev.following_count,
          is_following:
            typeof data?.is_following === "boolean"
              ? data.is_following
              : prev.is_following,
        };
      });
    } catch (err) {
      console.error("Follow action failed:", err);
      message.error(
        followStats.isFollowing
          ? "Unable to unfollow right now."
          : "Unable to follow this member right now."
      );
    } finally {
      setFollowLoading(false);
    }
  };

  const normalizeFollowResponse = (payload) => {
    if (!payload) {
      return { results: [], total: 0 };
    }
    if (Array.isArray(payload)) {
      return { results: payload, total: payload.length };
    }
    const results = Array.isArray(payload.results) ? payload.results : [];
    const total =
      typeof payload.count === "number" ? payload.count : results.length;
    return { results, total };
  };

  const fetchFollowersList = useCallback(
    async (page = 1) => {
      if (!dashboard?.username) {
        return;
      }
      setFollowersLoading(true);
      try {
        const { data } = await communityAPI.users.getFollowers(
          dashboard.username,
          {
            page,
            page_size: FOLLOW_PAGE_SIZE,
          }
        );
        const { results, total } = normalizeFollowResponse(data);
        setFollowersData(results);
        setFollowersTotal(total);
      } catch (err) {
        console.error("Failed to load followers:", err);
        message.error("Unable to load followers right now.");
      } finally {
        setFollowersLoading(false);
      }
    },
    [dashboard?.username]
  );

  const fetchFollowingList = useCallback(
    async (page = 1) => {
      if (!dashboard?.username) {
        return;
      }
      setFollowingLoading(true);
      try {
        const { data } = await communityAPI.users.getFollowing(
          dashboard.username,
          {
            page,
            page_size: FOLLOW_PAGE_SIZE,
          }
        );
        const { results, total } = normalizeFollowResponse(data);
        setFollowingData(results);
        setFollowingTotal(total);
      } catch (err) {
        console.error("Failed to load following list:", err);
        message.error("Unable to load following list right now.");
      } finally {
        setFollowingLoading(false);
      }
    },
    [dashboard?.username]
  );

  const fetchContributions = useCallback(
    async (page = 1, pageSize = 5) => {
      if (!dashboard?.username) {
        return;
      }
      setContributionsLoading(true);
      try {
        const { data } = await communityAPI.users.getContributions(
          dashboard.username,
          {
            type: projectTab,
            page,
            page_size: pageSize,
          }
        );
        const results = Array.isArray(data?.results) ? data.results : [];
        const total =
          typeof data?.count === "number" ? data.count : results.length;
        if (!results.length && total > 0 && page > 1) {
          setContributionsPage((prev) => (prev === page ? page - 1 : prev));
          return;
        }
        setContributionItems(results);
        setContributionsTotal(total);
      } catch (err) {
        console.error("Failed to load contributions:", err);
      } finally {
        setContributionsLoading(false);
      }
    },
    [dashboard?.username, projectTab]
  );

  const fetchBookmarks = useCallback(
    async (page = 1, pageSize = 5) => {
      if (!dashboard?.username) {
        return;
      }
      const isOwner =
        currentUser && currentUser.username === dashboard.username;
      if (!isOwner) {
        return;
      }
      setBookmarkLoading(true);
      try {
        const { data } = await communityAPI.users.getBookmarks(
          dashboard.username,
          {
            page,
            page_size: pageSize,
          }
        );
        const results = Array.isArray(data?.results) ? data.results : [];
        const total =
          typeof data?.count === "number" ? data.count : results.length;
        if (!results.length && total > 0 && page > 1) {
          setBookmarkPage((prev) => (prev === page ? page - 1 : prev));
          return;
        }
        setBookmarkItems(results);
        setBookmarksTotal(total);
      } catch (err) {
        console.error("Failed to load bookmarks:", err);
      } finally {
        setBookmarkLoading(false);
      }
    },
    [dashboard?.username, currentUser]
  );

  useEffect(() => {
    if (!dashboard || activeTab !== "projects") {
      return;
    }
    fetchContributions(contributionsPage, contributionsPageSize);
  }, [
    dashboard,
    activeTab,
    projectTab,
    contributionsPage,
    contributionsPageSize,
    fetchContributions,
  ]);

  useEffect(() => {
    if (!dashboard || activeTab !== "bookmarks") {
      return;
    }
    const isOwner = currentUser && currentUser.username === dashboard.username;
    if (!isOwner) {
      return;
    }
    fetchBookmarks(bookmarkPage, bookmarkPageSize);
  }, [
    dashboard,
    activeTab,
    bookmarkPage,
    bookmarkPageSize,
    fetchBookmarks,
    currentUser,
  ]);

  useEffect(() => {
    if (!followersDrawerOpen) {
      setFollowersPage(1);
      setFollowersData([]);
      return;
    }
    fetchFollowersList(followersPage);
  }, [followersDrawerOpen, followersPage, fetchFollowersList]);

  useEffect(() => {
    if (!followingDrawerOpen) {
      setFollowingPage(1);
      setFollowingData([]);
      return;
    }
    fetchFollowingList(followingPage);
  }, [followingDrawerOpen, followingPage, fetchFollowingList]);

  // Helper functions
  const resolveCategoryLabel = (category) => {
    if (!category) return "General";
    if (typeof category === "string") return category;
    const categoryMap = {
      general: "General",
      tech: "Technology",
      security: "Security",
      development: "Development",
      tutorial: "Tutorial",
    };
    return categoryMap[category] || category.name || category.slug || "General";
  };

  const getDifficultyColor = (difficulty) => {
    const value = (difficulty || "").toLowerCase();
    if (value === "easy") return "green";
    if (value === "medium") return "orange";
    if (value === "hard") return "red";
    return "blue";
  };

  const getTagColor = (tag) => {
    const value = (
      typeof tag === "string" ? tag : tag?.slug || tag?.name || ""
    ).toLowerCase();
    switch (value) {
      case "web":
        return "blue";
      case "crypto":
        return "purple";
      case "forensics":
        return "cyan";
      case "reverse":
        return "magenta";
      default:
        return "default";
    }
  };

  const formatTagLabel = (tag) => {
    if (!tag) return "";
    if (typeof tag === "string") return tag.toUpperCase();
    return (tag.name || tag.slug || "").toUpperCase();
  };

  const stripHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  };

  const formatFollowedAt = (value) => {
    if (!value) {
      return "—";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return "—";
    }
    return parsed.toLocaleDateString();
  };

  const isOwnProfile = !!(
    currentUser &&
    dashboard &&
    currentUser.username === dashboard.username
  );
  const solvedChallenges = dashboard?.solved_count || 0;
  const challengeScore = dashboard?.reputation || 0;
  const canAccessCertificate = false; // Disabled challenges feature
    // isOwnProfile && solvedChallenges > 0 && challengeScore > 0;

  useEffect(() => {
    if (!canAccessCertificate && showCertificate) {
      setShowCertificate(false);
    }
  }, [canAccessCertificate, showCertificate]);

  if (!authLoading && !currentUser && !username) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pt-24">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <LoginPrompt inline action="continue" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <div className="text-red-600 dark:text-red-400 text-xl text-center px-6">
          {error}
        </div>
      </div>
    );
  }

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pt-24">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <FullscreenSpinner message="Loading profile..." tone="slate" />
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-24">
        <div className="text-gray-600 dark:text-gray-300 text-xl text-center px-6">
          Profile information is not available.
        </div>
      </div>
    );
  }

  const contributionsSummary = dashboard?.Contributions || {};
  const blogCount = contributionsSummary.blog?.count ?? 0;
  const forumCount = contributionsSummary.forum?.count ?? 0;
  const learningCount = contributionsSummary.learning?.count ?? 0;
  const challengeCount = contributionsSummary.challenges?.count ?? 0;
  const bookmarkCount = contributionsSummary.bookmarks?.count ?? 0;
  const overviewSkills = Array.isArray(dashboard.skills)
    ? dashboard.skills
    : [];
  const activityFeed = dashboard.recentActivity || [];
  const achievementCards = dashboard.achievements || [];
  const sanitizedEmail = (dashboard.email || "").replace(/^mailto:/, "");
  const coverImage = dashboard.coverImage || dashboard.cover_url || null;
  const avatarImage = dashboard.avatar || dashboard.avatar_url || undefined;
  const joinedLabel = dashboard.joinDate || "N/A";
  const showCertificateModal = canAccessCertificate && showCertificate;

  const primaryStats = [
    {
      label: "Blog Posts",
      value: blogCount,
      icon: <FileText className="w-5 h-5" />,
      box: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
      action: () => navigate("/community/blog"),
    },
    {
      label: "Forum Threads",
      value: forumCount,
      icon: <MessageCircle className="w-5 h-5" />,
      box: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
      action: () => navigate("/community/forum"),
    },
    {
      label: "Learning Paths",
      value: learningCount,
      icon: <Library className="w-5 h-5" />,
      box: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
      action: () => navigate("/community/learning"),
    },
    //   action: () => navigate(isAdminUser ? "/community/challenges" : "/ctf"),
    // },
  ];
  const sidebarStats = [
    // {
    //   label: "Reputation",
    //   value: challengeScore,
    //   color: "bg-green-500",
    // },
    // {
    //   label: "Solved",
    //   value: solvedChallenges,
    //   color: "bg-yellow-500",
    // },
    {
      label: "Total Likes",
      value: dashboard.total_likes || 0,
      color: "bg-red-500",
    },
    {
      label: "Total Comments",
      value: dashboard.total_comments || 0,
      color: "bg-teal-500",
    },
    {
      label: "Total Views",
      value: dashboard.total_views || 0,
      color: "bg-orange-500",
    },
  ];
  const totalProjectItems =
    blogCount + forumCount + learningCount + challengeCount;
  const tabs = [
    { id: "overview", label: "Overview", count: overviewSkills.length },
    { id: "projects", label: "Projects", count: totalProjectItems },
    { id: "activity", label: "Activity", count: activityFeed.length },
    {
      id: "achievements",
      label: "Achievements",
      count: achievementCards.length,
    },
    ...(isOwnProfile
      ? [
        {
          id: "bookmarks",
          label: "Bookmarks",
          count: bookmarkCount,
        },
      ]
      : []),
    ...(isAdminUser
      ? [{ id: "moderation", label: "Moderation", count: pendingCount }]
      : []),
  ];
  const projectTabs = [
    { id: "blog", label: "Blog Posts", icon: "📝", count: blogCount },
    { id: "forum", label: "Forum", icon: "💬", count: forumCount },
    {
      id: "learning",
      label: "Learning",
      icon: "📚",
      count: learningCount,
    },
    // {
    //   id: "challenges",
    //   label: isAdminUser ? "Challenges" : "Solved Challenges",
    //   icon: isAdminUser ? "🎯" : "✅",
    //   count: challengeCount,
    // },
  ];
  const contactDetails = [
    {
      label: "Email",
      value: sanitizedEmail,
      href: dashboard.email,
    },
    {
      label: "Phone",
      value: dashboard.phone || "Not shared",
    },
    // {
    //   label: "Location",
    //   value: dashboard.location || "Remote",
    // },
    // {
    //   label: "Joined",
    //   value: joinedLabel,
    // },
  ].filter((detail) => detail.value);
  const professionalDetails = [
    // {
    //   label: "Title",
    //   value: dashboard.title || "Community Member",
    // },
    {
      label: "Username",
      value: `@${dashboard.username}`,
    },
    // {
    //   label: "Reputation",
    //   value: `${dashboard.reputation || 0} pts`,
    // },
    {
      label: "Contributions",
      value: `${totalProjectItems} published items`,
    },
  ];

  const statusInfo = dashboard.status || {};
  const statusSummary = dashboard.status_summary || {};
  const statusHeadline = statusSummary.headline || "In Good Standing";
  const statusDetail =
    statusSummary.detail ||
    "Community standing details are currently unavailable.";
  const statusSeverity = statusSummary.severity || "info";
  const statusBadgeList = Array.isArray(statusSummary.badges)
    ? Array.from(new Set(statusSummary.badges))
    : [];
  const statusRestrictionList = Array.isArray(statusSummary.restrictions)
    ? Array.from(new Set(statusSummary.restrictions))
    : [];
  const suspendedUntilLabel = statusSummary.suspended_until_display;
  const isBanned = Boolean(statusInfo.is_banned);
  const currentlySuspended = Boolean(statusInfo.currently_suspended);
  const followRestricted = isBanned || currentlySuspended;
  const postingEnabled =
    !isBanned && !currentlySuspended && statusInfo.can_post !== false;
  const commentingEnabled =
    !isBanned && !currentlySuspended && statusInfo.can_comment !== false;
  const statusLevelLabel =
    statusSummary.level_label ||
    {
      success: "Great Standing",
      warning: "Needs Attention",
      critical: "Critical",
      info: "Status Update",
    }[statusSeverity] ||
    "Status Update";

  const STATUS_VISUALS = {
    success: {
      icon: ShieldCheck,
      borderClass: "border-emerald-400/40 dark:border-emerald-500/30",
      iconClass: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-200",
      chipClass: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-200",
      surfaceClass:
        "bg-gradient-to-br from-emerald-50 via-white to-white dark:from-emerald-900/40 dark:via-gray-900 dark:to-gray-900",
    },
    warning: {
      icon: ShieldAlert,
      borderClass: "border-amber-400/40 dark:border-amber-500/30",
      iconClass: "bg-amber-500/15 text-amber-600 dark:text-amber-200",
      chipClass: "bg-amber-500/15 text-amber-700 dark:text-amber-200",
      surfaceClass:
        "bg-gradient-to-br from-amber-50 via-white to-white dark:from-amber-900/35 dark:via-gray-900 dark:to-gray-900",
    },
    critical: {
      icon: Ban,
      borderClass: "border-rose-400/40 dark:border-rose-500/30",
      iconClass: "bg-rose-500/15 text-rose-600 dark:text-rose-200",
      chipClass: "bg-rose-500/15 text-rose-700 dark:text-rose-200",
      surfaceClass:
        "bg-gradient-to-br from-rose-50 via-white to-white dark:from-rose-900/35 dark:via-gray-900 dark:to-gray-900",
    },
    info: {
      icon: Info,
      borderClass: "border-sky-400/40 dark:border-sky-500/30",
      iconClass: "bg-sky-500/15 text-sky-600 dark:text-sky-200",
      chipClass: "bg-sky-500/15 text-sky-700 dark:text-sky-200",
      surfaceClass:
        "bg-gradient-to-br from-sky-50 via-white to-white dark:from-sky-900/35 dark:via-gray-900 dark:to-gray-900",
    },
  };
  const statusVisual = STATUS_VISUALS[statusSeverity] || STATUS_VISUALS.info;
  const StatusIcon = statusVisual.icon;
  const STATUS_PILL_THEMES = {
    positive: {
      wrapper:
        "bg-emerald-500/20 text-emerald-100 border border-emerald-400/40",
      icon: "text-emerald-100",
    },
    warning: {
      wrapper: "bg-amber-500/25 text-amber-100 border border-amber-400/40",
      icon: "text-amber-100",
    },
    danger: {
      wrapper: "bg-rose-500/25 text-rose-100 border border-rose-400/50",
      icon: "text-rose-100",
    },
    info: {
      wrapper: "bg-white/20 text-white border border-white/30",
      icon: "text-white",
    },
  };
  const statusHeroPills = [];
  const pillLabels = new Set();
  const pushPill = (label, variant, IconComponent) => {
    if (!label || pillLabels.has(label)) {
      return;
    }
    pillLabels.add(label);
    statusHeroPills.push({
      label,
      variant,
      IconComponent,
    });
  };
  if (isBanned) {
    pushPill("Banned Account", "danger", Ban);
  }
  if (!isBanned && currentlySuspended) {
    pushPill("Suspended", "warning", ShieldAlert);
  }
  if (!postingEnabled) {
    pushPill("Posting Restricted", "warning", MegaphoneOff);
  }
  if (!commentingEnabled) {
    pushPill("Commenting Restricted", "warning", MessageSquareOff);
  }
  statusBadgeList.forEach((badge) => pushPill(badge, "positive", ShieldCheck));
  if (!statusHeroPills.length && !isBanned && !currentlySuspended) {
    pushPill("Active Member", "info", ShieldCheck);
  }


  console.log("Rendering UserProfile for:", statusInfo);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="relative h-80 overflow-hidden rounded-xl mt-16">
        {coverImage ? (
          <img
            src={coverImage}
            alt="Cover"
            className="w-full h-full object-cover rounded-xl"
          />
        ) : (
          <div className="w-full h-full rounded-xl bg-gradient-to-r from-[#229bb8] via-primary to-primary" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-start md:items-end gap-6">
              <div className="relative">
                <Avatar
                  src={avatarImage}
                  name={dashboard.name}
                  size={32}
                  className="border-4 border-white shadow-2xl"
                />
                
              </div>

              <div className="flex-1 text-white">
                <h1 className="text-4xl font-bold mb-2 flex items-center gap-2">
                  {dashboard.name || dashboard.username}
                  {(dashboard?.is_trusted || dashboard?.status?.is_trusted) && (
                    <img
                      src={verifiedBadge}
                      alt="Verified"
                      className="w-5 h-5 shrink-0 drop-shadow-sm translate-y-[1px]"
                    />
                  )}
                </h1>
                <p className="text-xl opacity-90 mb-2">
                  {dashboard.title || "Community Member"}
                </p>
                {!isAdminUser ||
                  (statusHeroPills.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {statusHeroPills.map(
                        ({ label, variant, IconComponent }) => {
                          const theme =
                            STATUS_PILL_THEMES[variant] ||
                            STATUS_PILL_THEMES.info;
                          return (
                            <span
                              key={label}
                              className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold backdrop-blur-sm ${theme.wrapper}`}
                            >
                              <IconComponent
                                className={`h-3.5 w-3.5 ${theme.icon}`}
                              />
                              {label}
                            </span>
                          );
                        }
                      )}
                    </div>
                  ))}
                <div className="flex flex-wrap items-center gap-4 text-sm opacity-80">
                  <span className="flex items-center gap-1">
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {dashboard.location || "Remote"}
                  </span>
                  <span>Member since {joinedLabel}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setFollowersDrawerOpen(true)}
                    className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold backdrop-blur-sm bg-white/10 text-white hover:bg-white/20 transition"
                  >
                    <Users className="h-3.5 w-3.5" />
                    <span>
                      <span className="font-bold">
                        {formatNumber(followStats.followers)}
                      </span>
                      <span className="ml-1 opacity-80">Followers</span>
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setFollowingDrawerOpen(true)}
                    className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold backdrop-blur-sm bg-white/10 text-white hover:bg-white/20 transition"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    <span>
                      <span className="font-bold">
                        {formatNumber(followStats.following)}
                      </span>
                      <span className="ml-1 opacity-80">Following</span>
                    </span>
                  </button>

                  {!isOwnProfile && (
                    <button
                      type="button"
                      onClick={handleFollowAction}
                      disabled={followLoading || followRestricted}
                      className={`
        inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold backdrop-blur-sm transition
        ${followLoading ? "cursor-wait" : ""}
        ${followRestricted ? "cursor-not-allowed opacity-50" : ""}
        ${
          followStats.isFollowing
            ? "bg-white/20 text-white hover:bg-white/30"
            : "bg-blue-500/90 text-white hover:bg-blue-600/90"
        }
      `}
                    >
                      {followLoading ? (
                        <span>...</span>
                      ) : followStats.isFollowing ? (
                        <>
                          <Check className="h-3.5 w-3.5" />
                          Following
                        </>
                      ) : (
                        <>
                          <Plus className="h-3.5 w-3.5" />
                          Follow
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                {isOwnProfile && (
                  <button
                    onClick={() => setShowCreate(!showCreate)}
                    className="px-6 py-3 bg-white/90 text-gray-900 rounded-xl font-semibold hover:bg-white transition-colors"
                  >
                    {showCreate ? "Close" : "Create Content"}
                  </button>
                )}
                {isOwnProfile && (
                  <button
                    onClick={() => navigate("/community/profile/edit")}
                    className="px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-[#229bb8] transition-colors"
                  >
                    Edit Profile
                  </button>
                )}
                {canAccessCertificate && (
                  <button
                    onClick={() => setShowCertificate(true)}
                    className="px-6 py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-700 transition-colors"
                  >
                    Get CTF Certificate
                  </button>
                )}
                {isOwnProfile && isAdminUser && (
                  <button
                    onClick={() => navigate("/admin/dashboard")}
                    className="px-6 py-3 bg-gray-900 text-white rounded-xl font-semibold hover:bg-gray-800 transition-colors"
                  >
                    Open Admin Panel
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8 -mt-16 relative z-10 px-2 lg:px-0">
          {primaryStats.map((stat) => (
            <button
              key={stat.label}
              onClick={stat.action}
              className="group relative flex items-center gap-4 p-4 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl border border-white/40 dark:border-gray-700/50 shadow-sm hover:shadow-lg transition-all duration-300"
            >
              <div className={`flex items-center justify-center p-3 rounded-xl transition-all duration-300 group-hover:scale-110 ${stat.box}`}>
                {stat.icon}
              </div>
              
              <div className="flex-1 text-left">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-0.5">
                  {stat.label}
                </p>
                <div className="flex items-baseline gap-2">
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {formatNumber(stat.value)}
                  </p>
                  <ExternalLink className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-all duration-300" />
                </div>
              </div>
            </button>
          ))}
        </div>

        {showCreate && isOwnProfile && (
          <Card className="p-8 mb-8 shadow-lg border-0">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Create New Content
                </h2>
                <p className="text-gray-500 dark:text-gray-400">
                  Share knowledge with the community
                </p>
              </div>
              <button
                onClick={() => setShowCreate(false)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white text-2xl transition-colors"
              >
                ✕
              </button>
            </div>
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <select
                value={createType}
                onChange={(e) => setCreateType(e.target.value)}
                className="w-full px-6 py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all font-semibold"
              >
                <option value="blog">Blog Post</option>
                <option value="forum">Discussion</option>
                {isAdminUser && <option value="challenge">Challenge</option>}
                <option value="learning">Learning Path</option>
              </select>
            </div>
            {createType === "blog" && <CreatePost type="blog" />}
            {createType === "forum" && <CreatePost type="forum" />}
            {createType === "challenge" && isAdminUser && <ChallengeCreate />}
            {createType === "learning" && (
              <LearningPathCreate
                onSave={() => {
                  /* could trigger refetch of profile */
                }}
                onCancel={() => { }}
              />
            )}
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 shrink-0 min-w-max px-4 py-3 rounded-lg font-medium transition-all duration-200 ${activeTab === tab.id
                      ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    }`}
                >
                  <span className="flex items-center justify-center gap-2 whitespace-nowrap">
                    {tab.label}
                    {typeof tab.count === "number" && (
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${activeTab === tab.id
                            ? "bg-primary/10 text-primary dark:bg-[#3db8d9]/20 dark:text-indigo-200"
                            : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                          }`}
                      >
                        {formatNumber(tab.count)}
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>

            {activeTab === "overview" && (
              <Card className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  About
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                  {dashboard.bio || "This member has not added a bio yet."}
                </p>
                {contactDetails.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
                    {contactDetails.map((item) => (
                      <div
                        key={item.label}
                        className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700"
                      >
                        <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                          {item.label}
                        </p>
                        {item.href ? (
                          <a
                            href={item.href}
                            className="text-base font-semibold text-primary dark:text-primary/70 break-all"
                          >
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-base font-semibold text-gray-900 dark:text-white">
                            {item.value}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  {professionalDetails.map((item) => (
                    <div
                      key={item.label}
                      className="p-4 rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-700 shadow-sm"
                    >
                      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                        {item.label}
                      </p>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Skills
                </h4>
                {overviewSkills.length ? (
                  <div className="flex flex-wrap gap-2">
                    {overviewSkills.map((skill) => (
                      <Badge key={skill} color="blue" className="px-3 py-1">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Share your expertise in your bio to showcase skills.
                  </p>
                )}
              </Card>
            )}

            {activeTab === "projects" && (
              <Card className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  My Contributions
                </h3>
                <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl mb-6">
                  {projectTabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setProjectTab(tab.id)}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${projectTab === tab.id
                          ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                          : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        }`}
                    >
                      {/* <span>{tab.icon}</span> */}
                      <span className="flex items-center gap-2">
                        {tab.label}
                        <span
                          className={`text-xs font-semibold px-2 py-0.5 rounded-full ${projectTab === tab.id
                              ? "bg-primary/10 text-primary dark:bg-[#3db8d9]/20 dark:text-indigo-200"
                              : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                            }`}
                        >
                          {formatNumber(tab.count)}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>

                <div className="space-y-3">
                  {contributionsLoading ? (
                    <div className="text-center py-10 text-gray-600 dark:text-gray-400">
                      Loading contributions...
                    </div>
                  ) : (
                    <>
                      {projectTab === "blog" &&
                        (contributionItems.length ? (
                          contributionItems.map((post) => (
                            <Link
                              key={post.id}
                              to={
                                post.slug
                                  ? `/community/${post.post_type || "blog"}/${post.slug
                                  }`
                                  : "#"
                              }
                              className="block"
                            >
                              <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800 hover:shadow-lg transition-all duration-200">
                                <div className="flex justify-between items-start mb-3">
                                  <h4 className="text-md font-semibold text-gray-900 dark:text-white line-clamp-2">
                                    {post.title}
                                  </h4>
                                  <Badge color="blue">{post.date || "-"}</Badge>
                                </div>

                                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                                  <span className="flex items-center gap-1">
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                      />
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                      />
                                    </svg>
                                    {formatNumber(post.views)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                                      />
                                    </svg>
                                    {formatNumber(post.likes)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                      />
                                    </svg>
                                    {formatNumber(post.comments || 0)}
                                  </span>
                                </div>
                              </div>
                            </Link>
                          ))
                        ) : (
                          <div className="text-center py-10 text-gray-600 dark:text-gray-400">
                            No blog posts yet
                          </div>
                        ))}

                      {projectTab === "forum" &&
                        (contributionItems.length ? (
                          contributionItems.map((post) => (
                            <Link
                              key={post.id}
                              to={
                                post.slug
                                  ? `/community/${post.post_type || "forum"}/${post.slug
                                  }`
                                  : "#"
                              }
                              className="block"
                            >
                              <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-800 hover:shadow-lg transition-all duration-200">
                                <div className="flex justify-between items-start mb-3">
                                  <h4 className="text-md font-semibold text-gray-900 dark:text-white line-clamp-2">
                                    {post.title}
                                  </h4>
                                  <Badge color="purple">
                                    {resolveCategoryLabel(post.category)}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                                  <span>
                                    {formatNumber(post.replies ?? 0)} replies
                                  </span>
                                  <span>{formatNumber(post.views)} views</span>
                                  <span>{post.date || "-"}</span>
                                </div>
                              </div>
                            </Link>
                          ))
                        ) : (
                          <div className="text-center py-10 text-gray-600 dark:text-gray-400">
                            No forum posts yet
                          </div>
                        ))}

                      {projectTab === "learning" &&
                        (contributionItems.length ? (
                          contributionItems.map((path) => (
                            <Link
                              key={path.id}
                              to={`/community/learning/${path.id}`}
                              className="block"
                            >
                              <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-200 dark:border-purple-800 hover:shadow-lg transition-all duration-200">
                                <div className="flex justify-between items-start mb-4">
                                  <div>
                                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
                                      {path.title}
                                    </h4>
                                    <Badge color="purple">
                                      {path.difficulty || "Learning"}
                                    </Badge>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-2xl font-bold text-secondary dark:text-purple-400">
                                      {formatNumber(path.completed ?? 0)}/
                                      {formatNumber(path.modules ?? 0)}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      Completed
                                    </div>
                                  </div>
                                </div>
                                <p className="text-gray-600 dark:text-gray-400">
                                  {path.description ||
                                    "Keep progressing through this path."}
                                </p>
                                <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                                  Progress: {formatNumber(path.progress ?? 0)}%
                                </div>
                              </div>
                            </Link>
                          ))
                        ) : (
                          <div className="text-center py-10 text-gray-600 dark:text-gray-400">
                            No learning paths yet
                          </div>
                        ))}

                      {projectTab === "challenges" &&
                        (contributionItems.length ? (
                          contributionItems.map((challenge) => (
                            <Link
                              key={challenge.id}
                              to={
                                isAdminUser && challenge.slug
                                  ? `/community/challenges/${challenge.slug}`
                                  : "/ctf"
                              }
                              className="block"
                            >
                              <div className="p-6 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl border border-orange-200 dark:border-orange-800 hover:shadow-lg transition-all duration-200">
                                <div className="flex justify-between items-start mb-3">
                                  <div>
                                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white line-clamp-2">
                                      {challenge.title}
                                    </h4>
                                    <div className="flex items-center gap-2 mt-2">
                                      <Badge
                                        color={getDifficultyColor(
                                          challenge.difficulty
                                        )}
                                      >
                                        {challenge.difficulty?.toUpperCase()}
                                      </Badge>
                                      {challenge.category && (
                                        <Badge color="blue">
                                          {resolveCategoryLabel(
                                            challenge.category
                                          )}
                                        </Badge>
                                      )}
                                      {Array.isArray(challenge.tags) &&
                                        challenge.tags
                                          .slice(0, 2)
                                          .map((tag) => (
                                            <Badge
                                              key={
                                                tag.id || tag.slug || tag.name
                                              }
                                              color={getTagColor(tag)}
                                            >
                                              {formatTagLabel(tag)}
                                            </Badge>
                                          ))}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                                      {formatNumber(challenge.points)}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      points
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                                  <span>{challenge.date || "-"}</span>
                                  {challenge.completed && (
                                    <span className="text-green-500">
                                      {isAdminUser ? "✓ Completed" : "✓ Solved"}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </Link>
                          ))
                        ) : (
                          <div className="text-center py-10 text-gray-600 dark:text-gray-400">
                            {isAdminUser
                              ? "No challenges yet"
                              : "No solved challenges yet"}
                          </div>
                        ))}

                      {contributionItems.length > 0 &&
                        contributionsTotal > contributionsPageSize && (
                          <div className="flex justify-center pt-4">
                            <Pagination
                              current={contributionsPage}
                              pageSize={contributionsPageSize}
                              total={contributionsTotal}
                              showSizeChanger
                              pageSizeOptions={["5", "10", "15"]}
                              onChange={(page, size) => {
                                setContributionsPage(page);
                                setContributionsPageSize(Number(size));
                              }}
                              onShowSizeChange={(_, size) => {
                                setContributionsPage(1);
                                setContributionsPageSize(Number(size));
                              }}
                              className="antd-pagination"
                            />
                          </div>
                        )}
                    </>
                  )}
                </div>
              </Card>
            )}

            {activeTab === "activity" && (
              <Card className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
                  Recent Activity
                </h3>
                <div className="relative">
                  {/* Vertical Timeline Line */}
                  <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-100 dark:bg-gray-800" />

                  <div className="space-y-8">
                    {activityFeed.length ? (
                      activityFeed.map((item, idx) => (
                        <div
                          key={item.id}
                          className="relative flex items-start gap-6 pl-1"
                        >
                          {/* Timeline Marker */}
                          <div className="relative z-10 w-10 h-10 flex-shrink-0 bg-white dark:bg-gray-900 rounded-full border-2 border-gray-100 dark:border-gray-800 flex items-center justify-center text-primary dark:text-primary/80 shadow-sm">
                            <span className="text-xs font-bold uppercase">
                              {item.type?.charAt(0) || "P"}
                            </span>
                          </div>

                          <div className="flex-1 pt-1">
                            <p className="font-semibold text-gray-900 dark:text-white text-lg mb-1">
                              {stripHtml(item.title)}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                              {item.date}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-10 text-gray-600 dark:text-gray-400">
                        No recent activity yet
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {activeTab === "achievements" && (
              <Card className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  Achievements
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {achievementCards.map((achievement, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-4 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800"
                    >
                      <div className="text-3xl">{achievement.icon}</div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">
                          {achievement.title}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {achievement.detail || achievement.date}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {isOwnProfile && activeTab === "bookmarks" && (
              <Card className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  Saved Bookmarks
                </h3>
                <div className="space-y-3">
                  {bookmarkLoading ? (
                    <div className="text-center py-10 text-gray-600 dark:text-gray-400">
                      Loading bookmarks...
                    </div>
                  ) : bookmarkItems.length ? (
                    <>
                      {bookmarkItems.map((post) => (
                        <Link
                          key={post.id}
                          to={
                            post.slug
                              ? `/community/${post.post_type || "blog"}/${post.slug
                              }`
                              : "#"
                          }
                          className="block"
                        >
                          <div className="p-6 bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800 hover:shadow-lg transition-all duration-200">
                            <div className="flex justify-between items-start mb-3">
                              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                                {post.title}
                              </h4>
                              <Badge color="yellow">{post.date || "-"}</Badge>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                              {stripHtml(post.excerpt) ||
                                "No description available."}
                            </p>
                            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                              <span className="flex items-center gap-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300 text-xs font-semibold">
                                  {post.post_type?.toUpperCase() || "POST"}
                                </span>
                                {post.category}
                              </span>
                              <span>{formatNumber(post.views)} views</span>
                              <span>{formatNumber(post.likes)} likes</span>
                              <span>
                                {formatNumber(post.comments)} comments
                              </span>
                            </div>
                          </div>
                        </Link>
                      ))}
                      {bookmarkItems.length > 0 &&
                        bookmarksTotal > bookmarkPageSize && (
                          <div className="flex justify-center pt-4">
                            <Pagination
                              current={bookmarkPage}
                              pageSize={bookmarkPageSize}
                              total={bookmarksTotal}
                              showSizeChanger
                              pageSizeOptions={["5", "10", "15"]}
                              onChange={(page, size) => {
                                setBookmarkPage(page);
                                setBookmarkPageSize(Number(size));
                              }}
                              onShowSizeChange={(_, size) => {
                                setBookmarkPage(1);
                                setBookmarkPageSize(Number(size));
                              }}
                              className="antd-pagination"
                            />
                          </div>
                        )}
                    </>
                  ) : (
                    <div className="text-center py-10 text-gray-600 dark:text-gray-400">
                      You have not bookmarked any posts yet.
                    </div>
                  )}
                </div>
              </Card>
            )}

            {activeTab === "moderation" && isAdminUser && (
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-lg p-0">
                <AdminDashboard embedded onAction={fetchPendingCount} />
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            {!isAdminUser && (
              <Card className="p-6 mb-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Account Standing
                  </h3>
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusVisual.chipClass}`}
                  >
                    {statusLevelLabel}
                  </span>
                </div>
                <div
                  className={`mt-4 rounded-2xl border ${statusVisual.borderClass} ${statusVisual.surfaceClass} p-5 shadow-sm`}
                >
                  <div className="flex flex-col gap-5">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex items-start gap-4">
                        <div
                          className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${statusVisual.iconClass}`}
                        >
                          <StatusIcon className="h-6 w-6" />
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {statusHeadline}
                          </h4>
                          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                            {statusDetail}
                          </p>
                          {suspendedUntilLabel && (
                            <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-200">
                              Suspends through {suspendedUntilLabel}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    {statusRestrictionList.length > 0 ? (
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                          Active Restrictions
                        </p>
                        <ul className="mt-2 space-y-2">
                          {statusRestrictionList.map((item) => (
                            <li
                              key={item}
                              className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
                            >
                              <Slash className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-200">
                        <ShieldCheck className="h-4 w-4" />
                        <span>No active restrictions in place.</span>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Impact Overview
                </h3>
                <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  Performance
                </span>
              </div>
              <div className="space-y-3">
                {sidebarStats.map((stat) => (
                  <div
                    key={stat.label}
                    className="flex items-center justify-between rounded-lg border border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900/70 px-4 py-3 backdrop-blur-sm"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`h-2.5 w-2.5 rounded-full ${stat.color}`}
                        aria-hidden="true"
                      />
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                        {stat.label}
                      </p>
                    </div>
                    <p className="text-xl font-semibold text-gray-900 dark:text-white">
                      {formatNumber(stat.value)}
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>
      <Drawer
        title="Followers"
        placement="right"
        width={420}
        open={followersDrawerOpen}
        onClose={() => setFollowersDrawerOpen(false)}
      >
        {followersLoading ? (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            Loading followers...
          </div>
        ) : followersData.length ? (
          <div className="space-y-3">
            {followersData.map((entry) => (
              <div
                key={entry.username || entry.id}
                className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white/70 p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900/60"
              >
                <Avatar
                  src={entry.avatar_url}
                  name={entry.name || entry.username}
                  size={10}
                  className="border border-white"
                />
                <div className="flex-1">
                  <Link
                    to={`/profile/${entry.username}`}
                    className="font-semibold text-gray-900 hover:text-primary dark:text-white"
                  >
                    {entry.name || entry.username}
                  </Link>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Following since {formatFollowedAt(entry.followed_at)}
                  </p>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  @{entry.username}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            No followers yet.
          </div>
        )}
        {followersTotal > FOLLOW_PAGE_SIZE && (
          <div className="mt-6 flex justify-center">
            <Pagination
              simple
              current={followersPage}
              pageSize={FOLLOW_PAGE_SIZE}
              total={followersTotal}
              onChange={(page) => setFollowersPage(page)}
            />
          </div>
        )}
      </Drawer>
      <Drawer
        title="Following"
        placement="right"
        width={420}
        open={followingDrawerOpen}
        onClose={() => setFollowingDrawerOpen(false)}
      >
        {followingLoading ? (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            Loading profiles...
          </div>
        ) : followingData.length ? (
          <div className="space-y-3">
            {followingData.map((entry) => (
              <div
                key={entry.username || entry.id}
                className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white/70 p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900/60"
              >
                <Avatar
                  src={entry.avatar_url}
                  name={entry.name || entry.username}
                  size={10}
                  className="border border-white"
                />
                <div className="flex-1">
                  <Link
                    to={`/profile/${entry.username}`}
                    className="font-semibold text-gray-900 hover:text-primary dark:text-white"
                  >
                    {entry.name || entry.username}
                  </Link>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Followed on {formatFollowedAt(entry.followed_at)}
                  </p>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  @{entry.username}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500 dark:text-gray-400">
            Not following anyone yet.
          </div>
        )}
        {followingTotal > FOLLOW_PAGE_SIZE && (
          <div className="mt-6 flex justify-center">
            <Pagination
              simple
              current={followingPage}
              pageSize={FOLLOW_PAGE_SIZE}
              total={followingTotal}
              onChange={(page) => setFollowingPage(page)}
            />
          </div>
        )}
      </Drawer>
      {showCertificateModal && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-8"
          onClick={() => setShowCertificate(false)}
        >
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowCertificate(false)}
              className="absolute -top-4 -right-4 bg-white hover:bg-gray-100 text-gray-900 rounded-full w-10 h-10 flex items-center justify-center text-xl font-bold shadow-lg z-10"
            >
              ✕
            </button>
            <div className="max-h-[95vh] overflow-auto bg-white rounded-lg shadow-2xl">
              <CertificatePreview />
            </div>
          </div>
        </div>
      )}
      {loginPromptAction && (
        <LoginPrompt
          action={loginPromptAction}
          onClose={() => setLoginPromptAction(null)}
        />
      )}
    </div>
  );
}
